<template>
  <v-dialog v-model="dialog" max-width="600" @click:outside="resetPlan">
    <v-card flat>
      <v-card-title>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon @click="resetPlan">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pb-0 pt-2">
        <template v-if="!saved">
          <v-row
            class="border--accent rounded-lg mx-0"
            v-bind:style="{
              backgroundColor: getColor($vuetify.theme.themes.light.accent, 0.1)
            }"
          >
            <v-col cols="12" class="secondary--text text-h5 font-weight-medium">
              {{ plan_def.name }} Plan
              <span class="performance--text text-h6" v-if="!isPaidPlan">
                14 day free trial
              </span>
            </v-col>
            <v-col cols="12">
              You've selected our {{ plan_def.name | lower }} plan. This plan
              can easily be canceled at anytime. The {{ plan_def.name }} plan
              includes all of the features listed below.
            </v-col>
            <v-col cols="12">
              <v-slide-group show-arrows>
                <v-slide-item v-for="(item, i) in bullets" :key="i" v-slot="{}">
                  <v-chip color="accent" class="mr-2">
                    <v-icon class="mr-2">{{ item.icon }}</v-icon>
                    {{ item.value }}
                  </v-chip>
                </v-slide-item>
              </v-slide-group>
            </v-col>
          </v-row>
          <v-row class="subtitle-1 rounded-lg border--lighttext mt-4 mx-0">
            <v-col>
              # of people added to CoreCounts:
            </v-col>
            <v-spacer class="limited-spacer"></v-spacer>
            <v-col cols="auto"> {{ billable_user_count }} people </v-col>
          </v-row>
          <v-row
            class="rounded-lg border--lighttext mt-4 mx-0"
            v-if="!billing.hasPaymentSource"
          >
            <v-col cols="12">
              <div id="card-element">
                <!-- A Stripe Element will be inserted here. -->
              </div>

              <!-- Used to display Element errors. -->
              <div role="alert">{{ error }}</div>
            </v-col>
          </v-row>
        </template>
        <div
          v-else
          class="secondary--text font-weight-medium text-h6 text-center"
        >
          <v-img
            :src="$images.CHECKOUT"
            width="85"
            class="mx-auto my-6"
          ></v-img>
          <div>Hey {{ profile.firstName || profile.email }},</div>
          <div v-if="isPaidPlan">
            Thanks for upgrading your account
          </div>
          <div v-else>
            Thanks for activating your
            <span class="performance--text">14 day free trial</span>
          </div>
          <v-divider class="mt-2"></v-divider>
        </div>
        <v-row :dense="saved" class="subtitle-1">
          <v-col>
            <template v-if="dueDate">Due {{ dueDate | longDate }}</template>
            <template v-else>Next invoice date</template>
          </v-col>
          <v-spacer> </v-spacer>
          <v-col cols="auto">
            {{ (plan_def.pricePerUser * billable_user_count) | currency }}
          </v-col>
        </v-row>
        <v-divider v-if="!saved"></v-divider>
        <v-row
          :dense="saved"
          class="text-h6 font-weight-medium secondary--text"
        >
          <v-col>
            Due today
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            $0.00
          </v-col>
        </v-row>
        <v-divider v-if="saved" class="pb-4"></v-divider>
      </v-card-text>
      <v-card-actions v-if="!saved">
        <v-btn
          :loading="saving"
          @click="save"
          color="performance"
          block
          class="mx-2"
          x-large
        >
          {{ isPaidPlan ? "Upgrade" : "Claim my free trial" }}
        </v-btn>
      </v-card-actions>
      <v-card-text v-if="dueDate">
        *The price for your bill on {{ dueDate | longDate }}, is an estimate of
        what you will be charged after the 14 day trial is over. This price can
        vary depending on the number of people in your account at the time of
        billing.
        <span class="text-decoration-underline"
          >We will remind you before your trial ends.</span
        >
      </v-card-text>
      <v-card-actions v-if="saved">
        <v-btn to="/" color="secondary" block class="mx-2" x-large>
          Back to dashboard
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";
import companyPlanTypes from "@/helpers/mixins/companyPlanTypes";
export default {
  name: "SelectPlanDialog",
  components: {},
  mixins: [companyPlanTypes],
  data: () => ({
    error: "",
    stripe: null,
    elements: null,
    card: null,
    dialog: false,
    valid: false,
    saving: false,
    saved: false,
    isPaidPlan: false,
    plan: {}
  }),
  computed: {
    title() {
      if (this.saved) {
        return "Order confirmation";
      }
      return this.isPaidPlan ? "Start your free trial" : "Upgrade your plan";
    },
    plan_def() {
      return (
        this.company_plan_types.find(x => x.id == this.plan.planTypeId) || {}
      );
    },
    bullets() {
      let list = [];
      let additionalBullet = this.getCompanyPlanTypeAdditionalBullet(
        this.plan_def.id
      );
      if (additionalBullet) {
        list.push({ icon: "add", value: additionalBullet });
      }
      list = list.concat(this.getCompanyPlanTypeBullets(this.plan_def.id));
      return list;
    },
    dueDate() {
      return this.isPaidPlan ? null : moment().add(14, "days");
    },
    ...mapGetters({
      company_plan_types: "lookup/company_plan_types",
      billing: "billing/billing",
      billable_user_count: "billing/billable_user_count",
      settings: "profile/settings",
      profile: "profile/profile"
    })
  },
  methods: {
    resetPlan() {
      this.dialog = false;
      this.plan = {};
      if (this.saved) {
        this.$emit("save");
      }
    },
    showDialog(planId) {
      this.plan = { planTypeId: planId };
      this.dialog = true;
      this.saved = false;
      this.isPaidPlan = this.settings.isPaidPlan;

      if (!this.billing.hasPaymentSource) {
        this.loadCard();
      }
    },
    loadCard() {
      loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY).then(res => {
        this.stripe = res;
        this.elements = this.stripe.elements();

        // Custom styling can be passed to options when creating an Element.
        const style = {
          base: {
            // Add your base input styles here. For example:
            fontSize: "16px",
            color: "#32325d"
          }
        };

        // Create an instance of the card Element.
        this.card = this.elements.create("card", { style });

        // Add an instance of the card Element into the `card-element` <div>.
        this.card.mount("#card-element");

        // Monitor change events on the Card Element to display any errors.
        this.card.on("change", ({ error }) => {
          if (error) {
            this.error = error.message;
          } else {
            this.error = "";
          }
        });
      });
    },
    async save() {
      this.saving = true;
      if (!this.billing.hasPaymentSource) {
        const { token, error } = await this.stripe.createToken(this.card);

        if (error) {
          // Inform the customer that there was an error.
          this.error = error.message;
          this.saving = false;
          return;
        } else {
          this.plan.tokenId = token.id;
        }
      }

      this.$store
        .dispatch("billing/upgrade", this.plan)
        .then(() => {
          this.$root.$snackbarSuccess("Plan upgraded successfully");
          this.$store.dispatch("profile/getProfile");
          this.saved = true;
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while upgrading your plan."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>
